"use client";

import React from "react";
import { cn } from "@heroui/react";
import { Icon } from "@iconify/react";

import { MarkdownWeBuild } from "@/components/MarkdownWeBuild";

export interface SystemMessageProps {
  content: string;
  timestamp?: string;
  className?: string;
}

/**
 * SystemMessage component for displaying system notifications in the chat.
 * These are typically routing info, status messages, or other non-conversational content.
 *
 * Styled to be visually distinct from user/assistant messages:
 * - Smaller text
 * - Muted colour
 * - No avatar
 * - No action buttons
 * - Subtle background
 * - Supports markdown rendering for formatted content
 */
export const SystemMessage: React.FC<SystemMessageProps> = ({ content, timestamp, className }) => {
  return (
    <div
      className={cn(
        "flex items-start gap-2 px-3 py-2 mx-auto max-w-2xl",
        "bg-default-100/50 dark:bg-default-50/30 rounded-medium",
        "border border-default-200/50 dark:border-default-100/20",
        className,
      )}
      data-testid="system-message"
    >
      <Icon
        className="text-default-400 mt-0.5 flex-shrink-0"
        icon="solar:info-circle-linear"
        width={16}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-default-500 dark:text-default-400 italic leading-relaxed [&_p]:m-0 [&_strong]:text-default-600 [&_strong]:dark:text-default-300">
          <MarkdownWeBuild>{content}</MarkdownWeBuild>
        </div>
        {timestamp && (
          <span className="text-xs text-default-400 dark:text-default-500 mt-1 block">
            {new Date(timestamp).toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  );
};

export default SystemMessage;
